import { Box, Grid, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { getRefundRequest, getRefundBreakdownRequest } from 'redux/reducers/refunds/reducer';
import { getRefund, getRefundLoader } from 'redux/reducers/refunds/selectors';
import { REFUNDS_TABLE_NAME, REFUND_BREAKDOWN_TABLE_NAME } from 'redux/reducers/refunds/constants';
import {
	getConfirmedOrdersRequest,
	getOrderDetailsCombinedRequest,
} from 'redux/reducers/orders/reducer';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import { PATHS } from 'routes/constants';
import { DeliveryInformation } from 'components/Order/DeliveryInformation';
import { OrderError } from 'components/Order/OrderError';
import { RefundItemsBreakdown } from './RefundItemsBreakdown';
import { RefundAmountBreakdown } from './RefundAmountBreakdown';
import { EasTableValue } from 'ui/EasTable/EasTableValue';
import { ETableStructureType } from 'services/constants/tableStructureTypes';
import { EasTooltip } from 'ui/EasTooltip';
import {
	getConfirmedOrders,
	getConfirmedOrdersLoader,
	getOrderDetailsCombined,
	getOrderDetailsCombinedLoader,
} from 'redux/reducers/orders/selectors';

export const Refund: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const params = useParams<{ orderId?: string; refundId?: string }>();
	const { orderId, refundId } = params;

	const orderDetailsCombined = useSelector(getOrderDetailsCombined);
	const orderDetailsCombinedIsLoading = useSelector(getOrderDetailsCombinedLoader);
	const orders = useSelector(getConfirmedOrders);
	const ordersIsLoading = useSelector(getConfirmedOrdersLoader);
	const order = orders?.data.find((order) => order.id === Number(orderId));

	// const refund = useSelector(getRefund);
	// const refundIsLoading = useSelector(getRefundLoader);
	const savedUrl = useSelector(getSavedUrl);

	const refundRow = orderDetailsCombined?.orderReturns?.find(
		(orderReturn) => orderReturn.id === Number(refundId),
	);

	// useEffect(() => {
	// 	if (!refundId) return;
	// 	dispatch(
	// 		getRefundRequest({
	// 			filter: [{ name: 'id', value: refundId }],
	// 			orderBy: ['id'],
	// 			page: { size: 1, from: 0 },
	// 			tableName: REFUNDS_TABLE_NAME,
	// 		}),
	// 	);
	// 	dispatch(
	// 		getRefundBreakdownRequest({
	// 			filter: [{ name: 'return_id', value: refundId }],
	// 			orderBy: ['return_id'],
	// 			page: { size: 1000, from: 0 },
	// 			tableName: REFUND_BREAKDOWN_TABLE_NAME,
	// 		}),
	// 	);
	// }, [dispatch, refundId]);

	// useEffect(() => {
	// 	if (!orderId) return;
	// 	dispatch(
	// 		getConfirmedOrdersRequest({
	// 			filters: [{ fieldName: 'id', value: String(orderId), operator: '=' }],
	// 		}),
	// 	);
	// }, [dispatch, orderId]);

	useEffect(() => {
		if (!orderId) return;
		dispatch(getOrderDetailsCombinedRequest({ orderId: String(orderId) }));
		dispatch(
			getConfirmedOrdersRequest({
				filters: [{ fieldName: 'id', value: orderId, operator: '=' }],
			}),
		);
	}, [dispatch, orderId]);

	const handleReplayHistory = () => {
		if (savedUrl?.includes(PATHS.REFUNDS_PAGE)) navigate(savedUrl);
		else navigate(PATHS.REFUNDS_PAGE);
	};

	return (
		<Box>
			<Box sx={{ padding: '10px 20px 0 20px', maxWidth: '100vw', overflowX: 'auto' }}>
				<Box sx={{ minWidth: '660px' }}>
					{!orderDetailsCombinedIsLoading && orderDetailsCombined?.orderReturns?.length === 0 && (
						<OrderError
							text="Refund not found"
							linkText="Go to the Refunds list"
							link={PATHS.REFUNDS_PAGE}
						/>
					)}

					{!!refundRow && !orderDetailsCombinedIsLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
							<Box sx={{ mb: 2 }}>
								<Box
									sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1 }}
								>
									<EasTooltip title="Go to the Refunds list" arrow>
										<IconButton onClick={handleReplayHistory} color="primary">
											<KeyboardBackspaceIcon />
										</IconButton>
									</EasTooltip>
									<Typography variant="h5" color="primary">
										Refund #{refundRow?.id} for order{' '}
									</Typography>
									<EasTableValue
										size="xxl"
										value={refundRow?.externalOrderId}
										type={ETableStructureType.OPEN_IN_NEW_TAB}
										tableStructureItem={{
											name: 'external_order_id',
											type: ETableStructureType.OPEN_IN_NEW_TAB,
											link: () => `/order/${refundRow?.orderId}`,
											tooltip: 'Open order details in new tab',
										}}
									/>
								</Box>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'start',
										gap: '20px',
									}}
								>
									{!!refundRow?.returnCreateDate && (
										<Typography variant="body2" gutterBottom color="GrayText">
											Refund created:{' '}
											{DateTime.fromISO(refundRow.returnCreateDate).toFormat('yyyy-MM-dd, HH:mm')}
										</Typography>
									)}
									{/* {!!refundRow?.confirmedDate && (
										<Typography variant="body2" gutterBottom color="GrayText">
											Reporting date:{' '}
											{DateTime.fromISO(refundRow.confirmedDate).toFormat('yyyy-MM-dd, HH:mm')}
										</Typography>
									)} */}
								</Box>
							</Box>
						</Box>
					)}
					{orderDetailsCombinedIsLoading && (
						<Box sx={{ mb: 2 }}>
							<Skeleton variant="rectangular" height="80px" sx={{ borderRadius: '12px' }} />
						</Box>
					)}
					<Grid container spacing={2}>
						<Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
							{!!refundRow && <RefundItemsBreakdown refundRow={refundRow} />}
							{/* The widget is mounted only once the refund is loaded, so until then its skeleton is shown here */}
							{!refundRow && orderDetailsCombinedIsLoading && (
								<Skeleton
									variant="rectangular"
									height="300px"
									sx={{ borderRadius: '12px', mb: 2 }}
								/>
							)}
						</Grid>

						<Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
							<DeliveryInformation />
							{!!refundRow && !!order && (
								<RefundAmountBreakdown refundRow={refundRow} orderRow={order} />
							)}
							{(!refundRow || !order) && (orderDetailsCombinedIsLoading || ordersIsLoading) && (
								<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px' }} />
							)}
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Box>
	);
};
